import { create } from 'zustand';
import type { ProductImage } from '@skytech/shared';

import { api } from '../../lib/api';

export interface WishlistItem {
  id: string;
  productId: string;
  addedAt: string;
  priceAtAdd: string;
  priceDropped: boolean;
  priceDrop: string | null;
  product: {
    id: string;
    name: string;
    slug: string;
    price: string;
    stockQuantity: number;
    image: ProductImage | null;
  };
}

interface WishlistState {
  ids: Set<string>;
  items: WishlistItem[];
  /** Lightweight id set, loaded on login to paint heart toggles across the catalog. */
  loadIds: () => Promise<void>;
  loadItems: () => Promise<void>;
  has: (productId: string) => boolean;
  add: (productId: string) => Promise<void>;
  remove: (productId: string) => Promise<void>;
  reset: () => void;
}

/**
 * Wishlist is authenticated-only (SRS §4.7); components gate the toggle behind a
 * sign-in prompt for guests. This store simply mirrors the server list.
 */
export const useWishlist = create<WishlistState>((set, get) => ({
  ids: new Set<string>(),
  items: [],

  async loadIds() {
    try {
      const res = await api.get<string[]>('/wishlist/ids');
      set({ ids: new Set(res.data) });
    } catch {
      set({ ids: new Set() });
    }
  },

  async loadItems() {
    const res = await api.get<WishlistItem[]>('/wishlist');
    set({ items: res.data, ids: new Set(res.data.map((i) => i.productId)) });
  },

  has(productId) {
    return get().ids.has(productId);
  },

  async add(productId) {
    const res = await api.post<WishlistItem[]>(`/wishlist/${productId}`);
    set({ items: res.data, ids: new Set(res.data.map((i) => i.productId)) });
  },

  async remove(productId) {
    const res = await api.delete<WishlistItem[]>(`/wishlist/${productId}`);
    set({ items: res.data, ids: new Set(res.data.map((i) => i.productId)) });
  },

  reset() {
    set({ ids: new Set(), items: [] });
  },
}));
